import { INTERLINK_TYPE, INTERLINK_SPECIAL_CASE } from "@/common/consts";

/**
 * Generates a path template to be used by the interlink module generator helper.
 *
 * @param {Array<string>} order Order represented by an array of strings of INTERLINK_TYPE keys that will be used to form the string template for the path to be generated
 * @param {Array<string>} keys Array of INTERLINK_TYPE keys representing which portions of the path will be generated
 * @param {string} dynamicKey INTERLINK_TYPE key which defines the string that will use the %_dynamic placeholder for the path to be generated
 * @returns {string} Path template
 */
const generatePathTemplate = (order, keys, dynamicKey) => {
	const pathTemplateSegments = [];

	// First we go through the order given, and then for any present value, and for the given dynamic key, we push the portion into the segment array.
	// Note that: for the `length parameter`, we need to make some adjustments as the parameters in the route need a different format.
	// This is to differentiate between routes like: `/%_dynamic-starts-%starts...` and `/starts-%_dynamic...`
	for (const index in order) {
		const key = order[index];
		let value;

		if (dynamicKey === order[index]) {
			value =
				key === INTERLINK_TYPE.LENGTH
					? `%_dynamic`
					: `${key}-%_dynamic`;
		} else if (keys.includes(key)) {
			value =
				key === INTERLINK_TYPE.LENGTH ? `%${key}` : `${key}-%${key}`;
		}

		if (value) {
			pathTemplateSegments.push(value);
		}
	}

	// Then lastly, we build the prefix of the path name
	// Also, note that: when the length parameter is present, the path we need to use is different than if we use one or more of the other three parameters (`starts`, `ends`, `with`).
	const pathPrefix =
		keys[0] === INTERLINK_TYPE.LENGTH ||
		dynamicKey === INTERLINK_TYPE.LENGTH
			? "letter-words"
			: "words-with-the-letter";

	return `/${pathPrefix}/${pathTemplateSegments.join("-")}/`;
};

/**
 * Generates a title template to be used by the interlink module generator helper.
 *
 * @param {Array<string>} keys Array of INTERLINK_TYPE keys representing which portions of the path will be generated
 * @param {string} suffixKey INTERLINK_TYPE key which defines the string that will determine which suffix we will use
 * @returns {string} Title template
 */
const generateTitleTemplate = (keys, suffixKey) => {
	const titleSegments = [];
	const keyTextsMap = {
		[INTERLINK_TYPE.LENGTH]: {
			phrase: `%${INTERLINK_TYPE.LENGTH} letter words`,
			suffix: " by length",
		},
		[INTERLINK_TYPE.STARTS]: {
			phrase: ` starting with %${INTERLINK_TYPE.STARTS}`,
			suffix: " starting with",
		},
		[INTERLINK_TYPE.ENDS]: {
			phrase: ` ending in %${INTERLINK_TYPE.ENDS}`,
			suffix: " ending in",
		},
		[INTERLINK_TYPE.WITH]: {
			phrase: ` containing %${INTERLINK_TYPE.WITH}`,
			suffix: " containing",
		},
	};

	// First we put the first segment into the segment array, which is always the same
	if (keys[0] !== INTERLINK_TYPE.LENGTH) {
		titleSegments.push("Words");
	}

	// Then for any present key, we grab each corresponding phrase from the keyTextsMap and put it into the segment array
	for (const key of keys) {
		titleSegments.push(keyTextsMap[key].phrase);
	}

	// Once we have the definitive elements in the segments array, then we work out where to put the `,` and the `and` interleaved into the segment array
	// Note that, the length of the array is going to be increasing as we splice it.... be carrefour
	if (titleSegments.length === 2 && suffixKey !== INTERLINK_TYPE.LENGTH) {
		titleSegments.splice(
			2,
			0,
			keys.includes(INTERLINK_TYPE.LENGTH) ? "," : " and" // small variation if there is length in the keys
		);
	} else if (titleSegments.length === 3) {
		titleSegments.splice(2, 0, " and");
		titleSegments.splice(4, 0, ",");
	} else if (titleSegments.length === 4) {
		titleSegments.splice(2, 0, ",");
		titleSegments.splice(4, 0, " and");
		titleSegments.splice(6, 0, ",");
	}

	titleSegments.push(keyTextsMap[suffixKey].suffix);

	return titleSegments.join("");
};

const fullInterlinks = {
	letters: "a,b,c,d,e,f,g,h,i,j,k,l,m,n,o,p,q,r,s,t,u,v,w,x,y,z",
	numbers: "2,3,4,5,6,7,8,9,10,11,12",
};

const cases = {
	/**
	 *
	 * @param {import("./interlink-module-generator").TGenerateInterlinkModule} generateInterlinkModule
	 * @return {{ title: string, groups: Array<import("./interlink-module-generator").TInterlinkModuleItem> }}
	 */
	[INTERLINK_SPECIAL_CASE.FALLBACK]: (generateInterlinkModule) => ({
		title: "Popular word lists",
		groups: [
			generateInterlinkModule({
				titleTemplate: "Words starting with",
				pathTemplate: "/words-that-start/%_dynamic/",
				whitelist: fullInterlinks.letters,
			}),
			generateInterlinkModule({
				titleTemplate: "Words ending in",
				pathTemplate: "/ending-with/%_dynamic/",
				whitelist: fullInterlinks.letters,
			}),
			generateInterlinkModule({
				titleTemplate: "Words containing",
				pathTemplate: "/words-with-the-letter/%_dynamic/",
				whitelist: fullInterlinks.letters,
			}),
			generateInterlinkModule({
				titleTemplate: "Words by length",
				pathTemplate: "/letter-words/%_dynamic/",
				whitelist: fullInterlinks.numbers,
			}),
		],
	}),
	/**
	 *
	 * @param {import("./interlink-module-generator").TGenerateInterlinkModule} generateInterlinkModule
	 * @return {{ title: string, groups: Array<import("./interlink-module-generator").TInterlinkModuleItem> }}
	 */
	[INTERLINK_SPECIAL_CASE.LENGTH_5]: (generateInterlinkModule) => ({
		title: "Popular 5 letter word lists",
		groups: [
			generateInterlinkModule({
				titleTemplate: "%length letter words starting with",
				pathTemplate: "/letter-words/%length-starts-%_dynamic/",
				pathSegments: { length: 5 },
				whitelist: fullInterlinks.letters,
			}),
			generateInterlinkModule({
				titleTemplate: "%length letter words ending in",
				pathTemplate: "/letter-words/%length-ends-%_dynamic/",
				pathSegments: { length: 5 },
				whitelist: fullInterlinks.letters.replace("j,", ""),
			}),
			generateInterlinkModule({
				titleTemplate: "%length letter words containing",
				pathTemplate: "/letter-words/%length-with-%_dynamic/",
				pathSegments: { length: 5 },
				whitelist: fullInterlinks.letters,
			}),
		],
	}),
};

/**
 * Generates interlink group modules for special cases
 *
 * @param {string} caseKey Valid key for a special case defined in cases above
 * @param {import("./interlink-module-generator").TGenerateInterlinkModule} generateInterlinkModule
 * @return {{ title: string, groups: Array<import("./interlink-module-generator").TInterlinkModuleItem> }}
 */
export const generateForSpecialCase = (caseKey, generateInterlinkModule) => {
	const fn = cases[caseKey];

	if (!fn) {
		return () => [];
	} // noop

	return fn(generateInterlinkModule);
};

/**
 * Generates interlink group modules based on the values given
 *
 * @param {{ starts: string, with: string, ends: string, length: string }} values Values entered in the finder form
 * @param {{ starts: string, with: string, ends: string, length: string }} interlinkWhitelists A collection of strings representing a whitelist containing letters or numbers separated by a ,
 * @param {import("./interlink-module-generator").TGenerateInterlinkModule} generateInterlinkModule Builder function
 * @param {Object} query
 * @return {{ title: string, groups: Array<import("./interlink-module-generator").TInterlinkModuleItem> }}
 */
export const generateInterlinkGroup = (
	values,
	interlinkWhitelists,
	generateInterlinkModule,
	query = {}
) => {
	const order = [
		INTERLINK_TYPE.LENGTH,
		INTERLINK_TYPE.STARTS,
		INTERLINK_TYPE.WITH,
		INTERLINK_TYPE.ENDS,
	];
	const keysWithValue = [];

	for (const key of order) {
		if (values[key]) {
			keysWithValue.push(key);
		}
	}

	const groups = [
		INTERLINK_TYPE.STARTS,
		INTERLINK_TYPE.ENDS,
		INTERLINK_TYPE.WITH,
		INTERLINK_TYPE.LENGTH,
	]
		.map((key) =>
			generateInterlinkModule({
				titleTemplate: generateTitleTemplate(keysWithValue, key),
				pathTemplate: generatePathTemplate(order, keysWithValue, key),
				whitelist: interlinkWhitelists[key],
				pathSegments: { ...values },
				query,
			})
		)
		.filter((g) => g !== undefined);

	// For the group title we can just do a magic trick, since the title
	// is very similar to any of the groups below, we just need to
	// make some simple replacements t oan existing title on one of the gorups
	// Conveniently we can use, for example, the title on the first group... ¯\_(ツ)_/¯
	const mainTitle = groups[0]?.title
		.replace(/starting with$|ending in$|containing$|by length$/, "")
		.replace(/and $|, $/, "")
		.replace("Words", "word lists")
		.replace("letter words", "letter word lists")
		.trim();

	return {
		title: mainTitle ? `Popular ${mainTitle}` : "",
		groups,
	};
};
